import { useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

// Store active reminder alarms (for interruption)
const activeAlarms = new Map(); // reminderId -> { timeoutId, repeatTimeoutId }

// Play a single chime sound
const playSingleChime = () => {
  return new Promise((resolve) => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) {
        resolve();
        return;
      }
      
      const audioCtx = new AudioContext();
      
      if (audioCtx.state === 'suspended') {
        audioCtx.resume();
      }
      
      const now = audioCtx.currentTime;
      
      const playChime = (freq, startTime, duration, volume) => {
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        
        const filter = audioCtx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(2000, startTime);
        
        oscillator.connect(filter);
        filter.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        
        oscillator.frequency.setValueAtTime(freq, startTime);
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      // Calm two-note chime
      playChime(523.25, now, 0.8, 0.5);
      playChime(783.99, now + 0.1, 0.7, 0.3);
      playChime(523.25, now + 0.5, 1.0, 0.3);
      
      setTimeout(() => {
        audioCtx.close();
        resolve();
      }, 1500);
      
    } catch (error) {

      resolve();
    }
  });
};

// Play sound 3 times with pauses
const playNotificationSound3Times = async () => {
  for (let i = 0; i < 3; i++) {
    await playSingleChime();
    if (i < 2) {
      await new Promise(r => setTimeout(r, 800)); // Pause between chimes
    }
  }
  
  // Vibrate on mobile
  try {
    if ('vibrate' in navigator) {
      navigator.vibrate([200, 100, 200, 100, 200]);
    }
  } catch (e) {
    // Vibration API not supported or blocked - non-critical, continue silently

  }
};

// Stop alarm for a specific reminder
export const stopReminderAlarm = (reminderId) => {
  const alarm = activeAlarms.get(reminderId);
  if (alarm) {
    if (alarm.timeoutId) clearTimeout(alarm.timeoutId);
    if (alarm.repeatTimeoutId) clearTimeout(alarm.repeatTimeoutId);
    activeAlarms.delete(reminderId);
    toast.dismiss(`reminder-toast-${reminderId}`);
    toast.success("Promemoria interrotto", { duration: 2000 });
    return true;
  }
  return false;
};

// Stop all active alarms
export const stopAllAlarms = () => {
  activeAlarms.forEach((alarm, reminderId) => {
    if (alarm.timeoutId) clearTimeout(alarm.timeoutId);
    if (alarm.repeatTimeoutId) clearTimeout(alarm.repeatTimeoutId);
    toast.dismiss(`reminder-toast-${reminderId}`);
  });
  activeAlarms.clear();
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!("Notification" in window)) {

    return false;
  }
  
  if (Notification.permission === "granted") {
    return true;
  }
  
  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }
  
  return false;
};

// Show notification with 3 sounds + repeat after 5 min + INTERROMPI button
export const showNotificationWithAlarm = (reminder, onNavigate) => {
  const reminderId = reminder.reminder_id;
  const reminderTime = new Date(reminder.datetime_iso);
  const timeStr = reminderTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  const dateStr = reminderTime.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  
  // Function to play sound sequence
  const playAlarmSequence = async () => {
    await playNotificationSound3Times();
  };
  
  // Play immediately (3 times)
  playAlarmSequence();
  
  // Schedule repeat after 5 minutes (300000ms)
  const repeatTimeoutId = setTimeout(() => {
    if (activeAlarms.has(reminderId)) {
      playAlarmSequence();
    }
  }, 300000); // 5 minutes
  
  // Store alarm reference
  activeAlarms.set(reminderId, { repeatTimeoutId });
  
  // Show browser notification
  if (Notification.permission === "granted") {
    try {
      const notification = new Notification(`🔔 ${reminder.title}`, {
        body: `${dateStr} alle ${timeStr}${reminder.description ? `\n${reminder.description}` : ''}`,
        icon: "/logo-transparent.png",
        badge: "/logo-transparent.png",
        tag: `reminder-${reminderId}`,
        requireInteraction: true,
        silent: true
      });
      
      notification.onclick = () => {
        stopReminderAlarm(reminderId);
        window.focus();
        if (onNavigate) onNavigate();
        notification.close();
      };
    } catch (error) {

    }
  }
  
  // Show toast with INTERROMPI button
  toast.info(`🔔 ${reminder.title}`, {
    id: `reminder-toast-${reminderId}`,
    description: `${dateStr} alle ${timeStr}`,
    duration: Infinity, // Don't auto-dismiss
    action: {
      label: "INTERROMPI",
      onClick: () => stopReminderAlarm(reminderId)
    },
    cancel: {
      label: "Vedi",
      onClick: () => {
        stopReminderAlarm(reminderId);
        if (onNavigate) onNavigate();
      }
    }
  });
  
  // Auto-stop after 10 minutes total
  const autoStopTimeout = setTimeout(() => {
    stopReminderAlarm(reminderId);
  }, 600000); // 10 minutes
  
  // Update alarm with auto-stop timeout
  const currentAlarm = activeAlarms.get(reminderId);
  if (currentAlarm) {
    currentAlarm.autoStopTimeout = autoStopTimeout;
  }
};

// Legacy showNotification for backward compatibility
export const showNotification = (title, body, onClick) => {
  playNotificationSound3Times();
  
  if (Notification.permission === "granted") {
    try {
      const notification = new Notification(title, {
        body,
        icon: "/logo-transparent.png",
        badge: "/logo-transparent.png",
        tag: `reminder-${Date.now()}`,
        requireInteraction: true,
        silent: true
      });
      
      if (onClick) {
        notification.onclick = () => {
          window.focus();
          onClick();
          notification.close();
        };
      }
      
      setTimeout(() => notification.close(), 30000);
      return notification;
    } catch (error) {

    }
  }
  return null;
};

// Hook to poll for upcoming reminders
export const useReminderNotifications = (enabled = true) => {
  const notifiedRef = useRef(new Set());
  
  const checkReminders = useCallback(async () => {
    if (!enabled) return;
    
    try {
      const response = await api.get('/reminders/upcoming');
      const reminders = response.data || [];
      
      for (const reminder of reminders) {
        if (notifiedRef.current.has(reminder.reminder_id)) continue;
        
        // Show notification with alarm system
        showNotificationWithAlarm(
          reminder,
          () => {
            window.location.href = '/calendario';
          }
        );
        
        // Mark as notified on server
        try {
          await api.post(`/reminders/${reminder.reminder_id}/notified`);
        } catch (e) {

        }
        
        notifiedRef.current.add(reminder.reminder_id);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn("Error checking reminders:", error);
      }
    }
  }, [enabled]);
  
  useEffect(() => {
    if (!enabled) return;
    
    requestNotificationPermission();
    checkReminders();
    
    const interval = setInterval(checkReminders, 15000);
    
    return () => {
      clearInterval(interval);
      stopAllAlarms(); // Cleanup on unmount
    };
  }, [enabled, checkReminders]);
  
  return { 
    checkReminders, 
    playSound: playNotificationSound3Times,
    stopAllAlarms 
  };
};

// Manual test function - plays 3 times like real notification
export const testNotificationSound = () => {
  playNotificationSound3Times();
};

// Reminder time options
export const REMINDER_BEFORE_OPTIONS = [
  { value: 0, label: "Al momento" },
  { value: 5, label: "5 minuti prima" },
  { value: 15, label: "15 minuti prima" },
  { value: 30, label: "30 minuti prima" },
  { value: 60, label: "1 ora prima" },
  { value: 120, label: "2 ore prima" },
  { value: 1440, label: "1 giorno prima" },
  { value: 2880, label: "2 giorni prima" }
];

// Recurrence type options
export const RECURRENCE_OPTIONS = [
  { value: "daily", label: "Ogni giorno" },
  { value: "weekly", label: "Ogni settimana" },
  { value: "monthly", label: "Ogni mese" }
];
